import React, { useEffect, useRef } from 'react';
import OpenSeadragon from 'openseadragon';
import styles from './DualViewer.scss';
import DualViewerToolbar from '../DualViewerToolbar/DualViewerToolbar';

const DualViewer = ({
  leftPlan,
  rightPlan,
  showLeft = true,
  showRight = true,
}) => {
  const viewerLeftRef = useRef(null);
  const viewerRightRef = useRef(null);

  const containerLeftRef = useRef(null);
  const containerRightRef = useRef(null);

  const leftLeadingRef = useRef(false);
  const rightLeadingRef = useRef(false);

  const baseOptions = {
    visibilityRatio: 0.98,
    constrainDuringPan: true,
    showNavigationControl: false,
    defaultZoomLevel: 0,
    minZoomLevel: 0,
    maxZoomLevel: 14,
    animationTime: 0.5,
    gestureSettingsMouse: {
      clickToZoom: false,
      flickEnabled: true,
      pinchToZoom: true,
      scrollToZoom: true,
    },
    gestureSettingsTouch: {
      clickToZoom: false,
      flickEnabled: true,
      pinchToZoom: true,
      scrollToZoom: true,
    },
  };

  // Init OpenSeadragon
  useEffect(() => {
    if (containerLeftRef.current && !viewerLeftRef.current) {
      viewerLeftRef.current = OpenSeadragon({
        ...baseOptions,
        element: containerLeftRef.current,
      });
    }

    if (containerRightRef.current && !viewerRightRef.current) {
      viewerRightRef.current = OpenSeadragon({
        ...baseOptions,
        element: containerRightRef.current,
      });
    }

    const v1 = viewerLeftRef.current;
    const v2 = viewerRightRef.current;
    if (!v1 || !v2) return;

    const v1Handler = () => {
      if (rightLeadingRef.current) return;
      leftLeadingRef.current = true;
      v2.viewport.zoomTo(v1.viewport.getZoom());
      v2.viewport.panTo(v1.viewport.getCenter());
      leftLeadingRef.current = false;
    };

    const v2Handler = () => {
      if (leftLeadingRef.current) return;
      rightLeadingRef.current = true;
      v1.viewport.zoomTo(v2.viewport.getZoom());
      v1.viewport.panTo(v2.viewport.getCenter());
      rightLeadingRef.current = false;
    };

    v1.addHandler('zoom', v1Handler);
    v1.addHandler('pan', v1Handler);
    v2.addHandler('zoom', v2Handler);
    v2.addHandler('pan', v2Handler);

    return () => {
      v1.removeHandler('zoom', v1Handler);
      v1.removeHandler('pan', v1Handler);
      v2.removeHandler('zoom', v2Handler);
      v2.removeHandler('pan', v2Handler);
    };
  }, []);

  useEffect(() => {
    const v1 = viewerLeftRef.current;
    if (v1 && leftPlan?.tiles) {
      v1.open({
        width: leftPlan.plan_width,
        height: leftPlan.plan_height,
        tileSize: 256,
        getTileUrl: (level, x, y) => leftPlan.tiles[[level, x, y].join('-')],
      });
    }
  }, [leftPlan]);

  useEffect(() => {
    const v2 = viewerRightRef.current;
    if (v2 && rightPlan?.tiles) {
      v2.open({
        width: rightPlan.plan_width,
        height: rightPlan.plan_height,
        tileSize: 256,
        getTileUrl: (level, x, y) => rightPlan.tiles[[level, x, y].join('-')],
      });
    }
  }, [rightPlan]);

  return (
    <div className={styles.wrapper}>
      <svg width='0' height='0' className={styles.svgDefs}>
        <defs>
          <filter id='mono_red'>
            <feColorMatrix
              type='matrix'
              values='
                0 0 0 0 1
                1 0 0 0 0
                1 0 0 0 0
                0 0 0 1 0
              '
            />
          </filter>
          <filter id='mono_blue'>
            <feColorMatrix
              type='matrix'
              values='
                1 0 0 0 0
                1 0 0 0 0
                0 0 0 0 1
                0 0 0 1 0
              '
            />
          </filter>
        </defs>
      </svg>

      <div
        ref={containerLeftRef}
        className={styles.canvas}
        style={{
          visibility: showLeft ? 'visible' : 'hidden',
          filter: 'url(#mono_red)',
        }}
      />
      <div
        ref={containerRightRef}
        className={styles.canvas}
        style={{
          visibility: showRight ? 'visible' : 'hidden',
          filter: 'url(#mono_blue)',
        }}
      />

      {viewerLeftRef?.current && viewerRightRef?.current && (
        <DualViewerToolbar
          v1={viewerLeftRef?.current}
          v2={viewerRightRef?.current}
        />
      )}
    </div>
  );
};

export default DualViewer;
